"use client";

import { useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import AnimatedNumber from "@/components/animated-number";
import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { coverageLogosForRegion } from "@/lib/coverage-logos";
import { formatPrice } from "@/lib/format-price";
import { regionFromPathname, type Region } from "@/lib/region";
import {
  resolveLink,
  resolveRichTextHref,
  type StoryblokLink,
} from "@/lib/storyblok-link";

type Asset = { filename?: string; alt?: string };

type PriceContent = {
  name?: string;
  description?: string;
  price_ie?: string;
  price_uk?: string;
  monthly_price_ie?: string;
  monthly_price_uk?: string;
  features?: string;
};

type PriceStory = {
  content?: PriceContent;
};

type BigStat = {
  top_label?: string;
  value?: string;
  bottom_label?: string;
  alt_label?: string;
};
type BigStatDataStory = {
  content?: { stats?: BigStat[] };
};

// `description` accepts both shapes: legacy plain string or migrated richtext.
type RichTextMark = {
  type: string;
  attrs?: { href?: string; target?: string };
};
type RichTextNode = {
  type: string;
  text?: string;
  content?: RichTextNode[];
  marks?: RichTextMark[];
};
type RichTextDoc = { type: "doc"; content?: RichTextNode[] };

export type ServiceHeroBlok = SbBlokData & {
  title?: string;
  subtitle?: string;
  description?: string | RichTextDoc;
  image?: Asset;
  stat_data?: string | BigStatDataStory;
  tag_label?: string;
  pricing_label?: string;
  price_ref?: string | PriceStory;
  whats_included_label?: string;
  cta_label?: string;
  cta_link?: StoryblokLink;
  cta_action?: BlokAction | "";
  is_not_treatment_page?: boolean;
  force_ie_pricing?: boolean;
};

export type ServiceHeroRating = {
  rating: number;
  totalReviews: number;
};

function resolvePrice(ref: ServiceHeroBlok["price_ref"]): PriceContent | undefined {
  if (!ref || typeof ref === "string") return undefined;
  return ref.content;
}

function StarRow() {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          viewBox="0 0 12 12"
          className="size-3 text-grape-03"
          fill="currentColor"
          aria-hidden
        >
          <path d="M6 .5l1.5 3.4 3.5.4-2.6 2.4.8 3.5L6 8.4 2.8 10.2l.8-3.5L.9 4.3l3.5-.4L6 .5z" />
        </svg>
      ))}
    </div>
  );
}

function ToggleIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
      className={`ml-2 shrink-0 transition-transform duration-200 ease-out ${
        open ? "rotate-45" : ""
      }`}
    >
      <path
        d="M10 4v12M4 10h12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="13"
      height="10"
      viewBox="0 0 13 10"
      fill="none"
      aria-hidden
      className="shrink-0 text-green-600"
    >
      <path
        d="M1.5 5l3.2 3.2L11.5 1.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function richTextHasContent(node: RichTextNode | undefined): boolean {
  if (!node) return false;
  if (node.type === "text") return !!node.text?.trim();
  return (node.content ?? []).some(richTextHasContent);
}

function renderHeroNode(
  node: RichTextNode,
  key: number,
  region?: Region,
): React.ReactNode {
  switch (node.type) {
    case "paragraph":
      return (
        <p key={key}>{node.content?.map((n, i) => renderHeroNode(n, i, region))}</p>
      );
    case "hard_break":
      return <br key={key} />;
    case "bullet_list":
      return (
        <ul key={key} className="ml-5 list-disc space-y-1">
          {node.content?.map((n, i) => renderHeroNode(n, i, region))}
        </ul>
      );
    case "ordered_list":
      return (
        <ol key={key} className="ml-5 list-decimal space-y-1">
          {node.content?.map((n, i) => renderHeroNode(n, i, region))}
        </ol>
      );
    case "list_item":
      return (
        <li key={key}>
          {node.content?.map((n, i) =>
            n.type === "paragraph"
              ? n.content?.map((c, j) => renderHeroNode(c, j, region))
              : renderHeroNode(n, i, region),
          )}
        </li>
      );
    case "text": {
      let el: React.ReactNode = node.text ?? "";
      for (const mark of node.marks ?? []) {
        if (mark.type === "bold") el = <strong>{el}</strong>;
        else if (mark.type === "italic") el = <em>{el}</em>;
        else if (mark.type === "link" && mark.attrs?.href && (node.text ?? "").trim()) {
          const href = resolveRichTextHref(mark.attrs.href, region);
          const isExternal = /^https?:\/\//i.test(href);
          const target = isExternal ? "_blank" : mark.attrs.target;
          el = (
            <a
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              className="text-purple-03 underline underline-offset-2 hover:opacity-80"
            >
              {el}
            </a>
          );
        }
      }
      return <span key={key}>{el}</span>;
    }
    default:
      return node.content?.map((n, i) => renderHeroNode(n, i, region)) ?? null;
  }
}

function HeroDescription({
  value,
  region,
}: {
  value: string | RichTextDoc;
  region?: Region;
}) {
  const baseClass = "text-body md:text-h6 leading-[1.64] text-[#4f4f4f] lg:leading-normal";
  if (typeof value === "string") {
    return <p className={baseClass}>{value}</p>;
  }
  if (!richTextHasContent(value)) return null;
  return (
    <div className={`flex flex-col gap-3 ${baseClass}`}>
      {value.content?.map((n, i) => renderHeroNode(n, i, region))}
    </div>
  );
}

export default function ServiceHeroView({
  blok,
  rating,
}: {
  blok: ServiceHeroBlok;
  rating: ServiceHeroRating | null;
}) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const priceRegion = blok.force_ie_pricing ? "ie" : region;
  const price = resolvePrice(blok.price_ref);
  const cardPrice = formatPrice(
    priceRegion === "uk" ? price?.price_uk : price?.price_ie,
    priceRegion,
  );
  const cardMonthly = formatPrice(
    priceRegion === "uk" ? price?.monthly_price_uk : price?.monthly_price_ie,
    priceRegion,
  );
  const cta = resolveLink(blok.cta_link, region);
  const triggerAction = useTriggerAction();
  const ctaAction = blok.cta_action || null;
  const showCta = Boolean(
    blok.cta_label && (ctaAction || cta.href !== "#"),
  );
  const ctaClass =
    "mt-2 hidden h-14 w-fit items-center justify-center rounded-[8px] bg-purple-03 px-7 text-body font-bold text-white transition-colors hover:bg-purple-04 md:inline-flex";

  const features = (price?.features || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  const hasFeatures = features.length > 0;
  const [whatsOpen, setWhatsOpen] = useState(false);


  const sharedStat =
    typeof blok.stat_data === "object" && blok.stat_data
      ? blok.stat_data.content?.stats?.[0]
      : undefined;
  const successLabel = "Success rates";
  const successValue = sharedStat?.value;
  const successDescription = sharedStat?.alt_label || sharedStat?.bottom_label;
  const hasSuccess = Boolean(successValue);
  const hasRating = Boolean(rating);
  const hasTag = Boolean(blok.tag_label);

  return (
   
    <section {...storyblokEditable(blok)} className="bg-white overflow-x-clip">
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-7 pb-12 md:max-lg:pt-12 lg:pt-[80px] lg:pb-[80px]">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-36">
          {blok.image?.filename && (
            <div className="order-first w-full lg:order-2 lg:ml-auto lg:mr-8 lg:w-[501px] lg:shrink-0">
              {/* Wrapper sized to the image so badges scale with it */}
              <div className="relative mx-auto aspect-501/548 w-[72%] md:max-lg:aspect-auto md:max-lg:h-[548px] md:max-lg:w-[88%] md:max-lg:max-w-[760px] lg:mx-0 lg:w-[501px]">
                <div className="absolute inset-0 overflow-hidden rounded-[24px]">
                  <Image
                    src={blok.image.filename}
                    alt={blok.image.alt || ""}
                    fill
                    sizes="(min-width: 1115px) 501px, (min-width: 768px) 760px, 72vw"
                    className="object-cover"
                    priority
                  />
                </div>

                {hasSuccess && (
                  <div className="absolute top-[-4%] right-[-16%] w-[50%] rounded-[8px] bg-purple-05 p-3 md:top-[-34px] md:max-lg:right-[-28px] md:w-[200px] md:rounded-[12px] md:p-5 lg:right-[-32px]">
                    <p className="text-[11px] font-medium tracking-[0.04em] text-purple-01 uppercase md:text-body">
                      {successLabel}
                    </p>
                    <p className="mt-0.5 text-h4 font-bold leading-none text-dusk-01 md:mt-1 md:text-h2">
                      <AnimatedNumber value={successValue} />
                    </p>
                    {successDescription && (
                      <p className="mt-0.5 text-[9px] leading-tight font-medium text-white md:mt-1 md:text-[12px]">
                        {successDescription}
                      </p>
                    )}
                  </div>
                )}

                {hasRating && rating && (
                  <div className="absolute top-[40%] left-[-16%] w-[40%] rounded-[6px] bg-[#e0bff0] px-2 py-1.5 md:top-[286px] md:left-[-34px] md:w-[160px] md:rounded-[8px] md:px-4 md:py-3">
                    <p className="text-button font-bold leading-none text-grape-03 md:text-body">
                      {rating.rating}
                    </p>
                    <div className="mt-0.5 md:mt-1">
                      <StarRow />
                    </div>
                    {rating.totalReviews > 0 && (
                      <p className="mt-0.5 text-caption text-grape-03 md:text-[12px]">
                        {rating.totalReviews.toLocaleString()} Google reviews
                      </p>
                    )}
                  </div>
                )}

                {hasTag && (
                  <div className="absolute bottom-[6%] right-[-12%] rounded-[6px] bg-[#dcfce7] px-3 py-1.5 text-center md:top-[469px] md:max-lg:right-[-31px] md:bottom-auto md:w-[293px] md:rounded-[8px] md:px-4 md:py-2.5 lg:right-[-32px]">
                    <p className="text-button font-bold whitespace-nowrap text-[#166534] md:text-[17px]">
                      {blok.tag_label}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-5 md:max-lg:max-w-[544px] lg:order-1 lg:max-w-[640px]">
            {blok.title && (
              <h1 className="text-h3 font-bold leading-[1.054] tracking-[0.02em] text-purple-04 md:text-[64px]">
                {blok.title}
              </h1>
            )}
            {blok.subtitle && (
              <p className="text-h6 font-bold leading-normal text-[#4f4f4f]">
                {blok.subtitle}
              </p>
            )}
            {blok.description && (
              <HeroDescription value={blok.description} region={region} />
            )}

            {(blok.pricing_label || price) && (
              <div className="mt-2 flex flex-col gap-5">
                <h2 className="text-h6 font-bold leading-normal text-purple-05">
                  {blok.pricing_label ||
                    (blok.is_not_treatment_page
                      ? "Pricing"
                      : "All inclusive pricing")}
                </h2>
                {price && blok.is_not_treatment_page && (
                  <div className="flex items-center justify-between gap-6 rounded-[16px] bg-dusk-01/50 p-5">
                    {price.name && (
                      <p className="text-h6 font-bold leading-[1.054] tracking-[0.02em] text-black">
                        {price.name}
                      </p>
                    )}
                    {cardPrice && (
                      <p className="text-h4 font-extrabold leading-[1.054] tracking-[0.02em] text-purple-03">
                        {cardPrice}
                      </p>
                    )}
                  </div>
                )}
                {price && !blok.is_not_treatment_page && (
                  <div className="flex flex-col gap-8 rounded-[16px] bg-dusk-01/50 p-5">
                    {price.name && (
                      <p className="text-h6 font-bold leading-[1.054] tracking-[0.02em] text-black">
                        {price.name}
                      </p>
                    )}
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      {cardPrice && (
                        <p className="text-h4 font-extrabold leading-[1.054] tracking-[0.02em] text-purple-03">
                          {cardPrice}
                        </p>
                      )}
                      {cardMonthly && (
                        <p className="text-button leading-[1.4] text-[#6b7280]">
                          or from {cardMonthly} per month
                        </p>
                      )}
                    </div>

                    {hasFeatures && (
                      <>
                        <div
                          className={`grid transition-[grid-template-rows,margin] duration-300 ease-in-out ${
                            whatsOpen
                              ? "grid-rows-[1fr]"
                              : "-mt-8 grid-rows-[0fr]"
                          }`}
                        >
                          <div className="overflow-hidden">
                            <div className="flex flex-col gap-8">
                              <ul className="grid gap-x-8 gap-y-1 lg:grid-cols-2">
                                {features.map((feat, i) => (
                                  <li
                                    key={`${i}-${feat}`}
                                    className="flex items-center gap-2.5"
                                  >
                                    <CheckIcon />
                                    <span className="text-body leading-[1.56] text-[#606060]">
                                      {feat}
                                    </span>
                                  </li>
                                ))}
                              </ul>

                              <div className="flex flex-col gap-2">
                                <p className="text-button leading-[1.4] text-[#6b7280]">
                                  Covered by
                                </p>
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                  {coverageLogosForRegion(region).map((logo) => (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                      key={logo.src}
                                      src={logo.src}
                                      alt={logo.alt}
                                      className={`${logo.heightClass} w-auto`}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setWhatsOpen((v) => !v)}
                          aria-expanded={whatsOpen}
                          className={`inline-flex cursor-pointer items-center self-start text-body font-extrabold leading-[1.054] text-purple-03 transition-[margin] duration-300 ease-in-out hover:underline ${
                            whatsOpen ? "mt-2" : ""
                          }`}
                        >
                          {whatsOpen
                            ? "Close"
                            : blok.whats_included_label || "What’s included"}
                          <ToggleIcon open={whatsOpen} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {showCta &&
              (ctaAction ? (
                <button
                  type="button"
                  onClick={(e) => triggerAction(ctaAction, e)}
                  className={`cursor-pointer ${ctaClass}`}
                >
                  {blok.cta_label}
                </button>
              ) : (
                <a
                  href={cta.href}
                  {...(cta.target ? { target: cta.target } : {})}
                  className={ctaClass}
                >
                  {blok.cta_label}
                </a>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}
